import { HttpClient } from '@angular/common/http';
import { OpenIdConfiguration, StsConfigLoader } from 'angular-auth-oidc-client';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OrgEntry } from './auth.model';
import { OidcOrgsCacheService } from './service/oidc-orgs-cache.service';

function isInvalidOidcOrgId(orgId: string): boolean {
    return !orgId || orgId.includes('REPLACE_WITH_');
}

export function buildOidcConfig(org: OrgEntry): OpenIdConfiguration {
    return {
        configId: org.clientId,
        authority: environment.AUTH_SERVICE_URL,
        clientId: org.clientId,
        redirectUrl: `${window.location.origin}/callback`,
        postLogoutRedirectUri: `${window.location.origin}`,
        responseType: 'code',
        scope: 'openid email profile offline_access',
        useRefreshToken: true,
        silentRenew: true,
        renewTimeBeforeTokenExpiresInSeconds: 300,
        issValidationOff: false,
        disableIdTokenValidation: false,
        autoUserInfo: false,
        maxIdTokenIatOffsetAllowedInSeconds: 600,
        secureRoutes: [environment.AUTH_SERVICE_URL, environment.API_GATEWAY_URL],
        authWellknownEndpoints: {
            issuer: environment.ZITADEL_ISSUER,
            authorizationEndpoint: `${environment.AUTH_SERVICE_URL}/oauth/v2/authorize`,
            tokenEndpoint: `${environment.AUTH_SERVICE_URL}/oauth/v2/token`,
            revocationEndpoint: `${environment.AUTH_SERVICE_URL}/oauth/v2/revoke`,
            userInfoEndpoint: `${environment.AUTH_SERVICE_URL}/oidc/v1/userinfo`,
            jwksUri: `${environment.AUTH_SERVICE_URL}/oauth/v2/keys`,
            endSessionEndpoint: `${environment.AUTH_SERVICE_URL}/oidc/v1/end_session`,
        },
    };
}

export class SingleOidcConfigLoader extends StsConfigLoader {
    constructor(
        private readonly http: HttpClient,
        private readonly orgsCacheService: OidcOrgsCacheService,
    ) {
        super();
    }

    loadConfigs(): Observable<OpenIdConfiguration[]> {
        const activeClientId = localStorage.getItem('oidc-active-clientId');

        if (isInvalidOidcOrgId(environment.OIDC_ORG_ID)) {
            return throwError(() => new Error(`Invalid OIDC_ORG_ID value: '${environment.OIDC_ORG_ID}'. Set a real org id in the selected environment file.`));
        }

        return this.http.get<OrgEntry[]>(`${environment.AUTH_SERVICE_URL}/auth/orgs?platform=${environment.OIDC_PLATFORM}&orgId=${environment.OIDC_ORG_ID}`).pipe(
            map((orgs) => {
                if (!orgs?.length) {
                    throw new Error(`No OIDC organizations returned for platform='${environment.OIDC_PLATFORM}' and orgId='${environment.OIDC_ORG_ID}'.`);
                }
                return orgs;
            }),
            tap((orgs) => this.orgsCacheService.set([...orgs].sort((a, b) => a.orgTag.localeCompare(b.orgTag)))),
            map((orgs) => {
                if (!activeClientId) {
                    return [];
                }
                const org = orgs.find((o) => o.clientId === activeClientId);
                if (!org) {
                    console.warn('[OIDC] Stored clientId does not match any org, clearing stale value', { activeClientId });
                    localStorage.removeItem('oidc-active-clientId');
                    return [];
                }
                return [buildOidcConfig(org)];
            }),
        );
    }
}

export const oidcConfigProvider = {
    provide: StsConfigLoader,
    useFactory: (http: HttpClient, orgsCacheService: OidcOrgsCacheService): SingleOidcConfigLoader => new SingleOidcConfigLoader(http, orgsCacheService),
    deps: [HttpClient, OidcOrgsCacheService],
};
